import {
  type DeclaredPageKey,
  type ProductDeclaration,
} from './product-declaration.js';

/**
 * Why a page is unavailable to an Operator (R4.5, R8.4, R14.2, R14.4).
 *
 * `not-configured` — the Product enables the page but declares no source for it (a
 * Product enabling no billing). `capability-absent` — the capability the page reads
 * through has not landed. `source-unreachable` — the source is configured and landed
 * but did not answer, computed at read time. The static reasons are computed at
 * composition; `source-unreachable` is a read-time classification.
 */
export type UnavailableReason =
  'not-configured' | 'capability-absent' | 'source-unreachable';

/**
 * Whether a page is available to an Operator, and — where not — why.
 *
 * One value per Declared_Page per Product, read by the navigation, the route guard and
 * the rendered page alike, so an unlanded capability, a Product that enabled no
 * source, and an unreachable source are the same "unavailable" to the Operator with
 * different reasons in the log — and a glance never reads an outage as a quiet period.
 */
export type PageAvailability =
  | { readonly state: 'available' }
  | { readonly state: 'unavailable'; readonly reason: UnavailableReason };

/**
 * Which capabilities have landed in this deployment, so the availability model can
 * report `capability-absent` for a page whose capability is not composed.
 *
 * In this build none of the Product-facing capabilities the Declared_Pages read
 * through — Billing, Support, Web_Analytics, Entitlements — is composed, so every
 * Declared_Page is `capability-absent`. A deployment that composes one flips its flag
 * and the pages it serves become `available` without a page-code change. A Bespoke_Page
 * depends on none of this: it is always available, being code the panel mounts.
 */
export interface LandedCapabilities {
  readonly billing: boolean;
  readonly support: boolean;
  readonly analytics: boolean;
  readonly entitlements: boolean;
}

/** The capability each Declared_Page reads through, for the capability-absent check. */
const PAGE_CAPABILITY: Record<DeclaredPageKey, keyof LandedCapabilities> = {
  overview: 'analytics',
  financials: 'billing',
  support: 'support',
  users: 'support',
  disputes: 'billing',
  'email-templates': 'support',
};

/**
 * The static availability of a Declared_Page for a Product (R14.2, R14.4).
 *
 * Computed at composition: a page the Product does not enable is not reached at all;
 * an enabled page whose capability has not landed is `capability-absent`; an enabled
 * page whose Product declares no source for it is `not-configured`; otherwise
 * `available`, subject to a read-time `source-unreachable` the page may still report.
 */
export function pageAvailability(
  product: ProductDeclaration,
  page: DeclaredPageKey,
  landed: LandedCapabilities,
): PageAvailability {
  if (!landed[PAGE_CAPABILITY[page]]) {
    return { state: 'unavailable', reason: 'capability-absent' };
  }

  // A Product enabling a billing/analytics page without the block is a validation
  // fault (never reached here), but a page whose configuration is genuinely absent in
  // a landed capability is `not-configured`.
  if (
    (page === 'financials' || page === 'disputes') &&
    product.billing === undefined
  ) {
    return { state: 'unavailable', reason: 'not-configured' };
  }
  if (page === 'overview' && product.analytics === undefined) {
    return { state: 'unavailable', reason: 'not-configured' };
  }

  return { state: 'available' };
}
